/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import { CloseButton, Button, Modal } from "react-bootstrap";
import FocusTrap from "focus-trap-react";
import {
  conditionsEngine,
  publish,
  subscribe,
  checkInDateRange,
  QuantumMetricsOnRageEvent,
  sleep,
} from "./util";
import { postEvent } from "./actions";
import { POPUP_CONTENT } from "./constants";

function Popup(props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  const [inDateRange, setIsInDateRange] = useState(false);

  const [bluePrintStyle, setStyle] = useState("");
  const [disableCloseButton, setDisableCloseButton] = useState("");
  const [title, setTitle] = useState("");
  const [eventQmReplayUrlOn, setEventQMReplayURL] = useState(false);
  const [body, setBody] = useState("");
  const [buttons, setButtons] = useState([]);
  const [render, setRender] = useState(false);

  const popupId = useRef("");
  const popupComponentTitle = useRef("");
  const customCloseButtonFunction = useRef(null);
  const hasClicked = useRef(false);
  const hasHovered = useRef(false);
  const hasMouseLeftViewport = useRef(false);
  const hasTimedOut = useRef(false);
  // need a ref to hold the mutation observer object outside the async function
  // to return the cleanup function
  const mutationObserver = useRef({});

  /**
   * Handles close function
   *
   * @param e - the event object
   * @param customFunction - custom code passed from the server
   *
   * @return {null}
   */
  const handleOnClick = (e, customFunction) => {
    setIsOpen(!isOpen);

    if (customFunction) {
      try {
        const custFunc = new Function(customFunction);
        custFunc();
      } catch (err) {
        console.log(err);
      }
    }

    if (
      !e.toElement &&
      !e.relatedTarget &&
      e.target.nodeName.toLowerCase() !== "select"
    ) {
      postEvent({
        component: "popup",
        title: popupComponentTitle.current,
        eventQmReplayUrlOn: eventQmReplayUrlOn,
        type: "click",
        button: e.target.innerHTML,
      });
    } else {
      postEvent({
        component: "popup",
        title: popupComponentTitle.current,
        eventQmReplayUrlOn: eventQmReplayUrlOn,
        type: "close",
      });
    }
  };

  const handleOnClickCondition = () => {
    publish(`popupClickEvent.${popupId.current}`);
  };

  const handleOnHoverCondition = () => {
    publish(`popupHoverEvent.${popupId.current}`);
  };

  //hooks version of componentDidUpdate
  useEffect(() => {
    (async () => {
      if (!props.definition) return;

      const { definition } = props;

      const {
        popupTitle,
        popupBody,
        captureEventQmReplayURL,
        popupDisplayConditions,
        popupCloseButtonCustomFunction,
        popupButtons: { buttons },
        id,
        title,
      } = definition[POPUP_CONTENT];

      //Quantum Metrics Event Listeners
      window?.QuantumMetricAPI?.addEventListener(-2, QuantumMetricsOnRageEvent);
      window?.QuantumMetricAPI?.addEventListener(
        -15,
        QuantumMetricsOnRageEvent
      );

      //Blueprint style either consumer or business (see https://massmutual.atlassian.net/wiki/spaces/CATS/pages/4254827192/Balto+Blueprint+Update)
      setStyle(popupDisplayConditions.blueprintStyle);
      if (captureEventQmReplayURL) setEventQMReplayURL(captureEventQmReplayURL);
      setDisableCloseButton(
        popupDisplayConditions.disableCloseButton ? "disableCloseButton" : ""
      );

      const idleTimeOut = popupDisplayConditions.idleTimeOut;
      let idleTime = 0;
      let isIdle = false;
      let idleInterval = 0;
      let idleForSeconds = idleTimeOut ? idleTimeOut * 1000 : 0;

      if (idleForSeconds >= 0) {
        const resetIdleTime = () => {
          idleTime = 0;
          isIdle = false;
        };
        //Idle Timeout events
        //Document
        document.addEventListener("onload", resetIdleTime, false);
        //Mouse
        document.addEventListener("click", resetIdleTime, false);
        document.addEventListener("mousedown", resetIdleTime, false);
        document.addEventListener("mouseup", resetIdleTime, false);
        document.addEventListener("mousemove", resetIdleTime, false);
        document.addEventListener("wheel", resetIdleTime, false);
        //Keyboard
        document.addEventListener("keydown", resetIdleTime, false);
        document.addEventListener("keyup", resetIdleTime, false);

        const checkIfIdle = () => {
          idleTime += 1000;

          if (idleTime >= idleForSeconds) {
            isIdle = true;
            setIsIdle(isIdle);
            clearInterval(idleInterval);
          }
        };
        if (idleForSeconds >= 0) {
          idleInterval = setInterval(checkIfIdle, 1000); // Check every second if user is idle
        }
      }

      if (popupTitle) setTitle(popupTitle);

      if (popupBody) setBody(popupBody);
      if (buttons) setButtons(buttons);

      if (popupTitle && popupBody && buttons) {
        setIsOpen(true);
        popupId.current = id;
        popupComponentTitle.current = title;
      }

      if (popupCloseButtonCustomFunction) {
        customCloseButtonFunction.current = popupCloseButtonCustomFunction;
      }

      //this section sets up the mutation observer
      //this will run everytime there is a change on the dom
      const target = document.documentElement || document.body;

      const mutationConfig = {
        attributes: true,
        childList: true,
        subtree: true,
      };

      const subscriber = () => {
        setRender(
          conditionsEngine(popupDisplayConditions, {
            ...engineOptions,
            isHoveredTrue: hasHovered.current,
            isClickTrue: hasClicked.current,
            isMouseLeftViewportTrue: hasMouseLeftViewport.current,
            isIdled: hasTimedOut.current,
          })
        );
      };

      const observer = new MutationObserver(subscriber);

      observer.observe(target, mutationConfig);

      mutationObserver.current = observer;
      //end section

      const engineOptions = {
        clickCallback: handleOnClickCondition,
        hoverCallback: handleOnHoverCondition,
      };

      subscribe(`popupClickEvent.${popupId.current}`, () => {
        if (!hasClicked.current) {
          hasClicked.current = true;
        }
        setRender(
          conditionsEngine(popupDisplayConditions, {
            ...engineOptions,
            isHoveredTrue: hasHovered.current,
            isClickTrue: hasClicked.current,
            isMouseLeftViewportTrue: hasMouseLeftViewport.current,
            isIdled: hasTimedOut.current,
          })
        );
      });

      subscribe(`popupHoverEvent.${popupId.current}`, () => {
        if (!hasHovered.current) {
          hasHovered.current = true;
        }
        setRender(
          conditionsEngine(popupDisplayConditions, {
            ...engineOptions,
            isHoveredTrue: hasHovered.current,
            isClickTrue: hasClicked.current,
            isMouseLeftViewportTrue: hasMouseLeftViewport.current,
            isIdled: hasTimedOut.current,
          })
        );
      });

      //quickly check for the mouse viewport options and set up the listener
      if (
        JSON.stringify(popupDisplayConditions).includes("mouse not in viewport")
      ) {
        const mouseLeaveCallback = (e) => {
          //that last condition exists because firefox/IE think a select element is outside the viewport
          if (
            e.clientY <= 0 ||
            e.clientX <= 0 ||
            e.clientX >= window.innerWidth ||
            e.clientY >= window.innerHeight
          ) {
            const isRender = conditionsEngine(popupDisplayConditions, {
              ...engineOptions,
              isHoveredTrue: hasHovered.current,
              isClickTrue: hasClicked.current,
              isMouseLeftViewportTrue: true,
            });

            if (isRender) {
              setRender(isRender);
              //clean up
              hasMouseLeftViewport.current = true;
              document.removeEventListener("mouseout", mouseLeaveCallback);
            }
          }
        };

        document.addEventListener("mouseout", mouseLeaveCallback);
      }

      // Check for a display condition date range. Set inDateRange appropriately.
      if (
        JSON.stringify(popupDisplayConditions).includes("beginDate") &&
        JSON.stringify(popupDisplayConditions).includes("endDate")
      ) {
        if (Object.keys(popupDisplayConditions.popupDateRange).length > 1) {
          const isInRange = checkInDateRange(
            popupDisplayConditions.popupDateRange
          );

          if (isInRange) {
            setIsInDateRange(isInRange);
          }
        }
      } else {
        setIsInDateRange(true);
      }

      if (popupDisplayConditions?.initialDelay) {
        await sleep(popupDisplayConditions.initialDelay);
      }

      setRender(
        conditionsEngine(popupDisplayConditions, {
          ...engineOptions,
          isHoveredTrue: hasHovered.current,
          isClickTrue: hasClicked.current,
          isMouseLeftViewportTrue: hasMouseLeftViewport.current,
          isIdled: hasTimedOut.current,
        })
      );
    })();

    return () => {
      mutationObserver.current?.disconnect();
    };
  }, [props?.definition]);

  useEffect(() => {
    if (render && inDateRange && isOpen && isIdle) {
      postEvent({
        component: "popup",
        title: popupComponentTitle.current,
        eventQmReplayUrlOn: eventQmReplayUrlOn,
        type: "open",
      });
    }
  }, [render, inDateRange, isOpen, isIdle]);

  const titleId = popupComponentTitle.current.replaceAll(" ", "-");

  return (
    render &&
    inDateRange && (
      <FocusTrap
        active={isOpen && isIdle}
        focusTrapOptions={{
          clickOutsideDeactivates: true,
          initialFocus: `#popupModal\\ ${titleId.replaceAll(".", "\\.")}\\ ${
            popupId.current
          } > div > div.modal-header > button`,
        }}
      >
        <aside
          aria-label={`popupModal-${titleId}-aside`}
          role={"complementary"}
        >
          {isOpen && isIdle && (
            <Modal.Dialog
              style={{
                position: "fixed",
                bottom: 0,
                right: "1rem",
                zIndex: 9999999,
              }}
              id={`popupModal ${titleId} ${popupId.current}`}
              aria-label={`popupModal-${titleId}-modal-dialog`}
              role={"dialog"}
              className={`mobile-popup balto-${bluePrintStyle}`}
            >
              <Modal.Header
                onHide={(e) =>
                  handleOnClick(e, customCloseButtonFunction.current)
                }
              >
                <Modal.Title
                  as="h3"
                  dangerouslySetInnerHTML={{ __html: title }}
                  id={"popupTitle"}
                />
                <CloseButton
                  data-dismiss="modal"
                  aria-label="Close"
                  id="baltoCloseButton"
                  className={disableCloseButton}
                  onClick={(e) =>
                    handleOnClick(e, customCloseButtonFunction.current)
                  }
                />
              </Modal.Header>
              <Modal.Body
                dangerouslySetInnerHTML={{ __html: body }}
                id={"popupBody"}
              />
              <Modal.Footer data-testid={"footer"} id={"popupFooter"}>
                {buttons.map((button, i) => (
                  <Button
                    id={`popup_cta_${
                      button.title
                        ? button.title.replace(/ /g, "_").toLowerCase()
                        : i
                    }`}
                    key={`button_${i}`}
                    variant={button.primaryStyle ? "primary" : "link"}
                    onClick={(e) => handleOnClick(e, button.customFunction)}
                    href={button.link ? button.link : null}
                    target={"blank"}
                    rel={"noopener noreferrer"}
                  >
                    {button.title}
                  </Button>
                ))}
              </Modal.Footer>
            </Modal.Dialog>
          )}
        </aside>
      </FocusTrap>
    )
  );
}

export default Popup;
